import ProfileImg from '@atoms/ProfileImg';
import Colors from '@constants/Colors';
import { yupResolver } from '@hookform/resolvers/yup';
import { Tick02Icon } from '@hugeicons/core-free-icons';
import HeaderButton from '@molecules/HeaderButton';
import ProfileSiteButtons from '@molecules/ProfileSiteButtons';
import {
  useUpdateProfile,
  useUpdateProfileLinks,
  useUpdateProfilePhoto,
} from '@queries/profiles';
import {
  useGetProfile,
  useGetUserAdminProfile,
  useUpdateUserPhoneNumber,
} from '@sunrise-ui/api-client';
import {
  type GenderPronouns,
  type ProfileLinks,
  type UpdateProfileLinksInput,
} from '@sunrise-ui/api/profile';
import { withAuthGate } from '@sunrise-ui/composites';
import {
  Button,
  Dropdown,
  Header,
  InputError,
  Loader,
  TextInput,
  TextInputWithCounter,
  isNative,
  useAuthStore,
  useDynamicStyles,
  useImagePicker,
  useMediaQueries,
  useSetHeader,
  useSnackbar,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { defaultBack } from '@utils/navigation';
import { type ImagePickerAsset } from 'expo-image-picker';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
  useWindowDimensions,
} from 'react-native';
import { object, string, type InferType } from 'yup';

export type AdditionalProfileInfo = {
  siteLinks?: ProfileLinks;
  profilePhoto?: ImagePickerAsset;
};

// the possible sites from ProfileLinks
export type ProfileSite = keyof UpdateProfileLinksInput;

type SiteOption = {
  label: string;
  value: ProfileSite;
};

const siteOptions: SiteOption[] = [
  { label: 'LinkedIn', value: 'linkedin' },
  { label: 'GitHub', value: 'github' },
  { label: 'X', value: 'x' },
  { label: 'Website', value: 'website' },
];

const schema = object({
  profilePhoto: object(),
  firstName: string()
    .required('Required')
    .max(20, 'Must be 20 characters or less'),
  lastName: string()
    .required('Required')
    .max(20, 'Must be 20 characters or less'),
  pronouns: string(),
  phoneNumber: string()
    .matches(
      /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/,
      'Must be a valid phone number',
    )
    .nullable()
    .notRequired(),
  headline: string()
    .required('Required')
    .max(40, 'Must be 40 characters or less'),
  bio: string().max(2500, 'Must be 2500 characters or less').notRequired(),
  siteLinks: object({
    linkedin: string()
      .matches(
        /^(https:\/\/)?www\.linkedin\.com\/in\/[\w-]+/,
        'Must be a valid LinkedIn URL like www.linkedin.com/in/communiful',
      )
      .notRequired()
      .nullable(),
    github: string()
      .matches(
        /^(https:\/\/)?(www\.)?github\.com\/[\w-]+/,
        'Must be a valid GitHub URL like github.com/communiful',
      )
      .notRequired()
      .nullable(),
    x: string()
      .matches(
        /^(https:\/\/)?(www\.)?x\.com\/[\w-]+/,
        'Must be a valid X URL like x.com/communiful',
      )
      .notRequired()
      .nullable(),
    website: string()
      .matches(
        /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})/,
        'Must be a valid URL like www.joincommuniful.com',
      )
      .notRequired()
      .nullable(),
  }).notRequired(),
});

type ProfileFormValues = InferType<typeof schema> & AdditionalProfileInfo;

function EditProfileScreen() {
  const { isMobile } = useMediaQueries();
  const { width: screenWidth } = useWindowDimensions();
  const styles = useDynamicStyles(createStyles, { screenWidth });
  const scrollViewRef = useRef<ScrollView>(null);
  const { showSnackbar } = useSnackbar();

  const { currentUserId } = useAuthStore();
  const pickImage = useImagePicker();
  const { isLoading, data: profile } = useGetProfile(currentUserId);
  const { isLoading: isLoadingAdminProfile, data: adminProfile } =
    useGetUserAdminProfile();
  const { mutateAsync: updateProfile } = useUpdateProfile();
  const { mutateAsync: updateProfileLinks } = useUpdateProfileLinks();
  const { mutateAsync: updateProfilePhoto } = useUpdateProfilePhoto();
  const { mutateAsync: updateUserPhoneNumber } = useUpdateUserPhoneNumber();

  const defaultValues = {
    firstName: profile?.first_name ?? '',
    lastName: profile?.last_name ?? '',
    pronouns: profile?.pronouns ?? '',
    phoneNumber: adminProfile?.phone_number ?? '',
    headline: profile?.headline ?? '',
    bio: profile?.bio || '',
    siteLinks: profile?.links,
    profilePhoto: {},
  };

  const isDataLoading = isLoading || isLoadingAdminProfile;

  const {
    handleSubmit,
    getValues,
    setValue,
    trigger,
    control,
    formState: { errors, isValid, isSubmitting },
    reset,
  } = useForm<ProfileFormValues, unknown, ProfileFormValues>({
    defaultValues,
    // @ts-expect-error - type doesn't like the nullable possibility
    resolver: yupResolver(schema),
  });
  const [editingSite, setEditingSite] = useState<ProfileSite>();
  const handleBack = defaultBack(`/to/${currentUserId}`);

  useSetHeader({
    header: () => (
      <Header
        headerLeft={<HeaderButton onPress={handleBack} />}
        headerRight={
          <HeaderButton
            icon={Tick02Icon}
            onPress={async () => {
              if (
                !editingSite &&
                Object.keys(errors.siteLinks ?? {}).length > 0
              ) {
                showSnackbar({
                  type: 'error',
                  text: `There is an issue with your ${Object.keys(errors.siteLinks ?? {})[0]} url`,
                });
              }
              await handleSubmit(saveProfile)();
            }}
            loading={isSubmitting}
          />
        }
      />
    ),
  });

  useEffect(() => {
    if (!isDataLoading) reset(defaultValues);
  }, [isDataLoading, profile, adminProfile]);

  const handlePickImage = async () => {
    const img = await pickImage();
    if (img) setValue('profilePhoto', img);
  };

  const saveProfile = async (data: ProfileFormValues) => {
    try {
      if (data) {
        if (data.siteLinks !== profile?.links) {
          await updateProfileLinks({
            linkedin: data.siteLinks?.linkedin,
            github: data.siteLinks?.github,
            x: data.siteLinks?.x,
            website: data.siteLinks?.website,
          });
        }

        if (data.profilePhoto.uri) await updateProfilePhoto(data.profilePhoto);

        // Update phone number in Cerebro (separate from profile service)
        // Only update if adminProfile loaded successfully to avoid clearing phone on fetch failure
        if (adminProfile && data.phoneNumber !== adminProfile.phone_number) {
          await updateUserPhoneNumber(data.phoneNumber || undefined);
        }

        await updateProfile({
          first_name: data.firstName,
          last_name: data.lastName,
          pronouns: (data.pronouns as GenderPronouns) || undefined,
          headline: data.headline,
          bio: data.bio || '',
        });

        handleBack();
      }
    } catch {
      return;
    }
  };

  const availableSiteOptions = siteOptions.filter(
    (so) => !getValues(`siteLinks.${so.value}`) || so.value === editingSite,
  );
  const hasAvailableSiteOptions = editingSite
    ? availableSiteOptions.length > 1
    : availableSiteOptions.length > 0;

  const scrollToEnd = () => {
    setTimeout(() => {
      scrollViewRef.current?.scrollToEnd();
    }, 100);
  };

  const addSiteLink = () => {
    setEditingSite(availableSiteOptions[0].value);
    scrollToEnd();
  };

  if (isDataLoading) return <Loader />;

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <ScrollView
        ref={scrollViewRef}
        contentContainerStyle={styles.container}
        keyboardDismissMode="on-drag"
      >
        <Controller
          control={control}
          render={() => (
            <ProfileImg
              userId={currentUserId}
              imgSize="large"
              styleSize={isMobile ? 150 : 225}
              tempUrl={getValues('profilePhoto')?.uri}
            />
          )}
          name="profilePhoto"
        />
        <View style={styles.buttons}>
          <Button onPress={handlePickImage}>Replace Photo</Button>
        </View>

        <View style={styles.inputSection}>
          <View>
            <Controller
              control={control}
              render={({ field }) => (
                <TextInput label="First Name" {...field} />
              )}
              name="firstName"
            />
            <InputError fieldError={errors.firstName} />
            <Controller
              control={control}
              render={({ field }) => <TextInput label="Last Name" {...field} />}
              name="lastName"
            />
            <InputError fieldError={errors.lastName} />
            <Controller
              control={control}
              render={({ field: { value, onChange } }) => (
                <Dropdown
                  label="Pronouns"
                  placeholder="Select pronouns"
                  options={[
                    { label: 'he/him', value: 'he/him' },
                    { label: 'she/her', value: 'she/her' },
                    { label: 'they/them', value: 'they/them' },
                  ]}
                  value={value}
                  onChange={onChange}
                  onClear={() => {
                    onChange('');
                  }}
                />
              )}
              name="pronouns"
            />
            <InputError fieldError={errors.pronouns} />
            <Controller
              control={control}
              render={({ field }) => (
                <TextInput
                  label="Phone Number"
                  keyboardType="phone-pad"
                  autoComplete="tel"
                  {...field}
                  value={field.value ?? ''}
                />
              )}
              name="phoneNumber"
            />
            <InputError fieldError={errors.phoneNumber} />
            <Controller
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextInputWithCounter
                  label="Headline"
                  maxLength={40}
                  value={value || ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  fieldError={errors.headline}
                />
              )}
              name="headline"
            />
            <Controller
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <TextInputWithCounter
                  label="Bio"
                  value={value || ''}
                  onChange={onChange}
                  onBlur={onBlur}
                  onFocus={scrollToEnd}
                  maxLength={2500}
                  multiline
                  numberOfLines={4}
                  textAlignVertical="top"
                  fieldError={errors.bio}
                  style={styles.bioField}
                />
              )}
              name="bio"
            />
          </View>
          <Controller
            control={control}
            render={({ field: { value, onChange } }) => (
              <View style={styles.siteLinkSection}>
                <ProfileSiteButtons
                  userId={currentUserId}
                  siteLinks={value}
                  onButtonPress={(siteLink: ProfileSite) => {
                    setEditingSite(siteLink);
                    scrollToEnd();
                  }}
                  onAddPress={hasAvailableSiteOptions ? addSiteLink : undefined}
                />
                {editingSite && (
                  <View style={styles.linkInputSection}>
                    <Dropdown
                      menuStyle={styles.dropdown}
                      notClearable
                      placeholder="Website"
                      options={availableSiteOptions}
                      value={editingSite}
                      onChange={(site) => {
                        setEditingSite(site);
                      }}
                    />
                    <View>
                      <TextInput
                        label="URL"
                        autoCapitalize="none"
                        value={value?.[editingSite] ?? ''}
                        onChange={(val) => {
                          onChange({
                            ...value,
                            [editingSite]: val,
                          });
                        }}
                        onBlur={(e) => {
                          const text = e.nativeEvent.text;
                          const isHttps = text.startsWith('https://');
                          const isHttp = text.startsWith('http://');

                          onChange({
                            ...value,
                            [editingSite]: isHttps
                              ? text
                              : isHttp
                                ? `https://${text.slice(7)}`
                                : `https://${text}`,
                          });
                        }}
                      />
                      <InputError
                        fieldError={errors.siteLinks?.[editingSite]}
                      />
                    </View>
                    <View style={styles.siteLinkButtons}>
                      <Button
                        onPress={() => {
                          onChange({ ...value, [editingSite]: undefined });
                          setEditingSite(undefined);
                        }}
                      >
                        Delete
                      </Button>
                      <Button
                        onPress={async () => {
                          await trigger(`siteLinks.${editingSite}`);
                          if (isValid) setEditingSite(undefined);
                        }}
                      >
                        Save
                      </Button>
                    </View>
                  </View>
                )}
              </View>
            )}
            name="siteLinks"
          />
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const createStyles = ({
  isMobile,
  isTablet,
  screenWidth,
}: WithResponsive<{ screenWidth: number }>) =>
  StyleSheet.create({
    container: {
      backgroundColor: Colors.dark.purple0,
      paddingHorizontal: isMobile ? 12 : 20,
      paddingBottom: 36,
      paddingTop: 0,
      width: screenWidth,
      alignItems: 'center',
      gap: 20,
    },
    buttons: {
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    },
    inputSection: {
      width: isMobile ? '100%' : isTablet ? '70%' : '50%',
    },
    siteLinkSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 27,
    },
    linkInputSection: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    dropdown: {
      marginBottom: 27,
    },
    siteLinkButtons: {
      justifyContent: 'center',
      display: 'flex',
      flexDirection: 'row',
      gap: 12,
    },
    bioField: {
      maxHeight: isNative ? 200 : undefined,
    },
  });

export default withAuthGate(EditProfileScreen);
